import Box from '@mui/material/Box';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { MapV2 } from '~components/Maps/v2/MapV2';
import { GeofencePickInfo, useGeofenceLayer } from '~hooks/useLiveMap/useGeofenceLayer';
import { Geofence, Site } from '~hooks/useSites/models';

import {
  calculateSiteBounds,
  extractAllPrimaryGeofences,
  extractSelectedSiteAdditionalGeofences,
  isValidCoordinates,
  SitePickInfo,
  SitesCircleGeofenceDatum,
  SitesPolygonGeofenceDatum,
  useSitesIconLayer,
  useSitesMapData,
} from '../hooks/useSitesMapData';
import { GeofenceHoverTooltip } from './GeofenceHoverTooltip';
import { SitePinHoverTooltip } from './SitePinHoverTooltip';
import { SitesSidebar } from './SitesSidebar';

const TARGET_ZOOM_ON_SELECTION = 15; // Target zoom level when selecting a site

interface SitesMapViewProps {
  sites: Site[];
  isLoading?: boolean;
  onEditSite: (siteId: string) => void;
  getAdditionalGeofences: (
    companyId: string,
    siteId: string,
    signal?: AbortSignal,
  ) => Promise<Geofence[] | undefined>;
  companyId: string;
  defaultMapCenter?: { lat: number; lng: number };
}

export function SitesMapView({
  sites,
  isLoading,
  onEditSite,
  getAdditionalGeofences,
  companyId,
  defaultMapCenter,
}: SitesMapViewProps) {
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [additionalGeofences, setAdditionalGeofences] = useState<Geofence[]>([]);
  const [isLoadingGeofences, setIsLoadingGeofences] = useState(false);
  // Track which site's geofences have been loaded to prevent race condition in pan effect
  const [geofencesReadyForSiteId, setGeofencesReadyForSiteId] = useState<string | null>(
    null,
  );
  const [hoveredGeofenceId, setHoveredGeofenceId] = useState<string | null>(null);
  const [hoveredGeofence, setHoveredGeofence] = useState<{
    geofence: SitesCircleGeofenceDatum | SitesPolygonGeofenceDatum;
    isCircle: boolean;
    position: { x: number; y: number };
  } | null>(null);
  const [hoveredSitePin, setHoveredSitePin] = useState<{
    site: Site;
    position: { x: number; y: number };
  } | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const hasInitiallyPanned = useRef(false);

  const { siteLayerData, getSiteById } = useSitesMapData(sites, {
    activeMarkerId: selectedSiteId,
  });

  // Filter sites for sidebar to only show sites that appear on the map (have valid coordinates)
  const sidebarSites = useMemo(() => {
    return sites.filter(
      (site) => isValidCoordinates(site.lat, site.lng) && !site.movingSite,
    );
  }, [sites]);

  const selectedSite = selectedSiteId ? getSiteById(selectedSiteId) : null;

  // Fetch additional geofences when a site is selected
  useEffect(() => {
    const controller = new AbortController();

    const loadAdditionalGeofences = async (siteId: string, siteCompanyId: string) => {
      // Clear ready state for new selection to prevent premature panning
      setGeofencesReadyForSiteId(null);
      setIsLoadingGeofences(true);

      try {
        const geofences = await getAdditionalGeofences(
          siteCompanyId,
          siteId,
          controller.signal,
        );
        if (controller.signal.aborted) return;

        setAdditionalGeofences(geofences || []);
        // Mark geofences as ready for this specific site
        setGeofencesReadyForSiteId(siteId);
      } catch (error) {
        if (controller.signal.aborted) return;

        console.error('Failed to fetch additional geofences:', error);
        setAdditionalGeofences([]);
        // Still mark as ready so map can pan to site without geofences
        setGeofencesReadyForSiteId(siteId);
      }

      setIsLoadingGeofences(false);
    };

    if (selectedSiteId && companyId) {
      void loadAdditionalGeofences(selectedSiteId, companyId);
    } else {
      setAdditionalGeofences([]);
      setGeofencesReadyForSiteId(null);
    }

    return () => controller.abort();
  }, [selectedSiteId, companyId]); // Note: getAdditionalGeofences intentionally excluded - it has an unstable reference

  // Filter site layer data - only show selected site's pin when one is selected
  const filteredSiteLayerData = useMemo(() => {
    if (selectedSiteId) {
      return siteLayerData.filter((datum) => datum.id === selectedSiteId);
    }
    return siteLayerData;
  }, [siteLayerData, selectedSiteId]);

  // Primary geofences - filtered to selected site when one is selected
  const primaryGeofences = useMemo(() => {
    if (selectedSite) {
      // When a site is selected, only show that site's primary geofence
      return extractAllPrimaryGeofences([selectedSite]);
    }
    // When no site selected, show all primary geofences
    return extractAllPrimaryGeofences(sites);
  }, [sites, selectedSite]);

  // Additional geofences for selected site only
  const selectedSiteAdditionalGeofences = useMemo(() => {
    if (!selectedSite || isLoadingGeofences) {
      return { circleGeofences: [], polygonGeofences: [] };
    }
    return extractSelectedSiteAdditionalGeofences(selectedSite, additionalGeofences);
  }, [selectedSite, additionalGeofences, isLoadingGeofences]);

  // Combine: primary + selected site's additional
  const combinedGeofences = useMemo(() => {
    return {
      circleGeofences: [
        ...primaryGeofences.circleGeofences,
        ...selectedSiteAdditionalGeofences.circleGeofences,
      ].sort((a, b) => b.radiusMeters - a.radiusMeters),
      polygonGeofences: [
        ...primaryGeofences.polygonGeofences,
        ...selectedSiteAdditionalGeofences.polygonGeofences,
      ],
    };
  }, [primaryGeofences, selectedSiteAdditionalGeofences]);

  // Geofence hover handler with coordinate conversion
  // DeckGL provides canvas-relative coords, but fixed positioning needs viewport-relative coords
  const handleGeofenceHover = useCallback((info: GeofencePickInfo | null) => {
    if (info) {
      const rect = mapContainerRef.current?.getBoundingClientRect();
      const offsetX = rect?.left ?? 0;
      const offsetY = rect?.top ?? 0;

      setHoveredGeofenceId(info.geofence.id);
      setHoveredGeofence({
        geofence: info.geofence as SitesCircleGeofenceDatum | SitesPolygonGeofenceDatum,
        isCircle: info.isCircle,
        position: { x: info.x + offsetX, y: info.y + offsetY },
      });
    } else {
      setHoveredGeofenceId(null);
      setHoveredGeofence(null);
    }
  }, []);

  // Geofence click handler - selects the parent site and opens edit modal
  const handleGeofenceClick = useCallback(
    (info: GeofencePickInfo) => {
      const geofence = info.geofence as
        | SitesCircleGeofenceDatum
        | SitesPolygonGeofenceDatum;
      setSelectedSiteId(geofence.siteId);
      onEditSite(geofence.siteId);
    },
    [onEditSite],
  );

  // Create DeckGL layers for geofences
  const { layers: geofenceLayers } = useGeofenceLayer(
    combinedGeofences.circleGeofences,
    combinedGeofences.polygonGeofences,
    {
      onHover: handleGeofenceHover,
      onClick: handleGeofenceClick,
      hoveredGeofenceId,
    },
  );

  // Handle site pin click - select site and open edit modal directly
  const handleSitePinClick = useCallback(
    (info: SitePickInfo) => {
      const siteId = info.site.id;
      setSelectedSiteId(siteId);
      onEditSite(siteId);
    },
    [onEditSite],
  );

  // Handle site pin hover - show tooltip with coordinate conversion
  const handleSitePinHover = useCallback(
    (info: SitePickInfo | null) => {
      if (info) {
        const rect = mapContainerRef.current?.getBoundingClientRect();
        const offsetX = rect?.left ?? 0;
        const offsetY = rect?.top ?? 0;
        const site = getSiteById(info.site.id);
        if (site) {
          setHoveredSitePin({
            site,
            position: { x: info.x + offsetX, y: info.y + offsetY },
          });
        }
      } else {
        setHoveredSitePin(null);
      }
    },
    [getSiteById],
  );

  // Create DeckGL layer for site pins using custom hook
  const siteLayers = useSitesIconLayer(filteredSiteLayerData, {
    activeMarkerId: selectedSiteId,
    onClick: handleSitePinClick,
    onHover: handleSitePinHover,
  });

  // Combined overlay layers for DeckGL
  const overlayLayers = useMemo(() => {
    return [...geofenceLayers, ...siteLayers];
  }, [geofenceLayers, siteLayers]);

  // Handle card click in sidebar
  // Note: Map fitting is handled by the useEffect that watches selectedSite and additionalGeofences
  const handleSiteSelect = useCallback(
    (siteId: string) => {
      // Toggle selection if clicking the same site
      if (siteId === selectedSiteId) {
        setSelectedSiteId(null);
        return;
      }

      setSelectedSiteId(siteId);
    },
    [selectedSiteId],
  );

  // Handle map background click (deselect site)
  const handleMapClick = useCallback(() => {
    // Only deselect if clicking the map background (not a pin)
    // The pin click handler will handle its own logic
    setSelectedSiteId(null);
  }, []);

  // Handle sidebar collapse toggle
  const handleToggleCollapse = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  // Capture map instance when loaded
  const handleMapLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
      // Only pan to default center on INITIAL load (prevents re-centering after modal close)
      if (defaultMapCenter && !hasInitiallyPanned.current) {
        map.panTo(defaultMapCenter);
        hasInitiallyPanned.current = true;
      }
    },
    [defaultMapCenter],
  );

  // Pan to site when selection changes and geofences are confirmed ready
  // Centers on the site pin and zooms to target level, then adjusts to show geofences if needed
  // Using geofencesReadyForSiteId instead of isLoadingGeofences to prevent race conditions
  useEffect(() => {
    if (selectedSite && mapRef.current && geofencesReadyForSiteId === selectedSite.id) {
      const siteLat = selectedSite.lat;
      const siteLng = selectedSite.lng;

      if (isValidCoordinates(siteLat, siteLng)) {
        // Step 1: Center on site and zoom to target level
        mapRef.current.panTo({ lat: siteLat!, lng: siteLng! });
        mapRef.current.setZoom(TARGET_ZOOM_ON_SELECTION);

        // Step 2: After zoom completes, check if geofences fit and adjust if needed
        const bounds = calculateSiteBounds(selectedSite, additionalGeofences);

        if (bounds) {
          setTimeout(() => {
            const googleBounds = new google.maps.LatLngBounds(
              { lat: bounds.south, lng: bounds.west },
              { lat: bounds.north, lng: bounds.east },
            );

            // Check if geofences fit at target zoom
            const currentBounds = mapRef.current?.getBounds();
            const ne = googleBounds.getNorthEast();
            const sw = googleBounds.getSouthWest();

            if (
              currentBounds &&
              (!currentBounds.contains(ne) || !currentBounds.contains(sw))
            ) {
              // Zoom out further to fit geofences
              mapRef.current?.fitBounds(googleBounds, 100);

              // Re-center on site after fitBounds
              setTimeout(() => {
                mapRef.current?.panTo({ lat: siteLat!, lng: siteLng! });
              }, 100);
            }
          }, 100);
        }
      }
    }
  }, [selectedSite, additionalGeofences, geofencesReadyForSiteId]);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        height: 'calc(100vh - 200px - var(--tread-agent-dock-height, 0px))',
        minHeight: 400,
      }}
    >
      <SitesSidebar
        sites={sidebarSites}
        selectedSiteId={selectedSiteId}
        onSiteSelect={handleSiteSelect}
        onSiteEdit={onEditSite}
        isCollapsed={isCollapsed}
        onToggleCollapse={handleToggleCollapse}
        isLoading={isLoading}
      />
      <Box
        ref={mapContainerRef}
        sx={{
          position: 'relative',
        }}
      >
        <MapV2
          overlayLayers={overlayLayers}
          center={defaultMapCenter}
          onClick={handleMapClick}
          emptyText={
            sidebarSites.length === 0
              ? t('administration.site.no_sites_found')
              : undefined
          }
          maxZoom={TARGET_ZOOM_ON_SELECTION}
        >
          <MapInstanceCapture onMapLoad={handleMapLoad} />
        </MapV2>
        {hoveredGeofence && (
          <GeofenceHoverTooltip
            geofence={hoveredGeofence.geofence}
            isCircle={hoveredGeofence.isCircle}
            position={hoveredGeofence.position}
          />
        )}
        {hoveredSitePin && (
          <SitePinHoverTooltip
            site={hoveredSitePin.site}
            position={hoveredSitePin.position}
          />
        )}
      </Box>
    </Box>
  );
}

/**
 * Helper component to capture the map instance.
 * MapV2 passes the map instance to its children via props.
 */
interface MapInstanceCaptureProps {
  map?: google.maps.Map;
  onMapLoad: (map: google.maps.Map) => void;
}

function MapInstanceCapture({ map, onMapLoad }: MapInstanceCaptureProps) {
  useEffect(() => {
    if (map) {
      onMapLoad(map);
    }
  }, [map, onMapLoad]);

  return null;
}
